import * as Joi from 'joi';
import { BusinessModeOfPayments } from '../enums/business-mode-payments';
import { Currency } from '../enums/retrieve-transaction.enums';
const stringWithNumbersOnlyRegEx = /^\d+$/; // @ref https://github.com/sideway/joi/issues/1157#issuecomment-293538673

const ModeOfPaymentSchema = Joi.number()
  .valid(...Object.values(BusinessModeOfPayments))
  .required()
  .messages({
    'any.only': 'Mode of payments is invalid.',
    'any.required': 'Mode of payments is required.',
    'number.base': 'Mode of payments is invalid.',
  });

export const GeneralInfoNonStarterSchema = Joi.object({
  merchantName: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Company name is required.',
    'string.base': 'Company name is invalid.',
    'string.empty': 'Company name is invalid.',
    'string.min': 'Company name can be at most 64 characters only.',
    'string.max': 'Company name can be at most 64 characters only.',
  }),
  merchantBusinessName: Joi.string()
    .trim()
    .min(1)
    .max(500)
    .required()
    .messages({
      'any.required': 'Business name is required.',
      'string.base': 'Business name is invalid.',
      'string.empty': 'Business name is invalid.',
      'string.min': 'Business name can be at most 500 characters only.',
      'string.max': 'Business name can be at most 500 characters only.',
    }),
  businessHandle: Joi.any(),
  merchantCompanyTin: Joi.string()
    .trim()
    .length(12)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'TIN is required.',
      'string.base': 'TIN is invalid.',
      'string.empty': 'TIN is invalid.',
      'string.length': 'TIN must be 12 digits.',
      'string.pattern.base': 'TIN must only contain digits',
    }),
  mainOfficeContactNumber: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Contact number is required.',
      'string.base': 'Contact number is invalid.',
      'string.empty': 'Contact number is invalid.',
      'string.pattern.base': 'Contact number is invalid.',
      'string.min': 'Contact number can be at most 30 characters only.',
      'string.max': 'Contact number can be at most 30 characters only.',
    }),
  mainOfficeBldgNameNo: Joi.string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .allow('')
    .messages({
      'string.base': 'Building name and number is invalid.',
      'string.empty': 'Building name and number is invalid.',
      'string.min':
        'Building name and number can be at most 200 characters only.',
      'string.max':
        'Building name and number can be at most 200 characters only.',
    }),
  mainOfficeStreetAddress: Joi.string()
    .trim()
    .min(1)
    .max(64)
    .required()
    .messages({
      'any.required': 'Street address is required.',
      'string.base': 'Street address is invalid.',
      'string.empty': 'Street address is invalid.',
      'string.min': 'Street address can be at most 64 characters only.',
      'string.max': 'Street address can be at most 64 characters only.',
    }),
  mainOfficeBarangay: Joi.string().trim().min(1).max(200).required().messages({
    'any.required': 'Barangay is required.',
    'string.base': 'Barangay is invalid.',
    'string.empty': 'Barangay is invalid.',
    'string.min': 'Barangay can be at most 200 characters only.',
    'string.max': 'Barangay can be at most 200 characters only.',
  }),
  mainOfficeCity: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'City is required.',
    'string.base': 'City is invalid.',
    'string.empty': 'City is invalid.',
    'string.min': 'City can be at most 100 characters only.',
    'string.max': 'City can be at most 100 characters only.',
  }),
  mainOfficeProvince: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Province is required.',
    'string.base': 'Province is invalid.',
    'string.empty': 'Province is invalid.',
    'string.min': 'Province can be at most 64 characters only.',
    'string.max': 'Province can be at most 64 characters only.',
  }),
  mainOfficePostalCode: Joi.string()
    .trim()
    .min(4)
    .max(10)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Postal code is required.',
      'string.base': 'Postal code is invalid.',
      'string.empty': 'Postal code is invalid.',
      'string.min': 'Postal code must have 4 to 10 characters only.',
      'string.max': 'Postal code must have 4 to 10 characters only.',
      'string.pattern.base': 'Postal code is invalid.',
    }),

  isBrickAndMortarStore: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  industry: Joi.string().trim().min(1).max(200).required().messages({
    'any.required': 'Industry is required.',
    'string.base': 'Industry is invalid.',
    'string.empty': 'Industry is invalid.',
    'string.min': 'Industry must have 1 to 200 characters only.',
    'string.max': 'Industry must have 1 to 200 characters only.',
  }),
  serviceDescription: Joi.string().trim().required().messages({
    'any.required': 'Service description is required.',
    'string.base': 'Service description is invalid.',
    'string.empty': 'Service description is invalid.',
  }),
  modeOfPayment: Joi.array()
    .items(ModeOfPaymentSchema)
    .min(1)
    .required()
    .messages({
      'any.required': 'Mode of payments is required.',
      'array.base': 'Mode of payments must be an array.',
      'array.includesRequiredUnknowns': 'Mode of payments has invalid items.',
      'array.min': 'Please select at least 1 mode of payment',
    }),
  currency: Joi.number().valid(Currency.PHP, Currency.USD).required().messages({
    'any.only': 'Currency is invalid.',
    'any.required': 'Currency is required.',
    'number.base': 'Currency is invalid.',
  }),
  aubPartnerId: Joi.string().trim().min(0).optional().messages({
    'string.base': 'AUB partner ID is invalid.',
    'string.empty': 'AUB partner ID is invalid.',
  }),

  estimatedMonthlyGrossTransaction: Joi.string()
    .trim()
    .min(0)
    .optional()
    .messages({
      'string.base': 'Estimated Monthly Gross Transaction is invalid.',
      'string.empty': 'Estimated Monthly Gross Transaction is invalid.',
    }),
  businessWebsiteUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Business website is invalid.',
    'string.empty': 'Business website is invalid.',
  }),
  isBusinessWebsiteUnderDevelopment: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  facebookUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Facebook field is invalid.',
    'string.empty': 'Facebook field is invalid.',
  }),
  twitterUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Twitter field is invalid.',
    'string.empty': 'Twitter field is invalid.',
  }),
  instagramUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Instagram field is invalid.',
    'string.empty': 'Instagram field is invalid.',
  }),
  tiktokUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Tiktok field is invalid.',
    'string.empty': 'Tiktok field is invalid.',
  }),

  businessTypeId: Joi.any(),

  aFirstName: Joi.any(),
  aMiddleName: Joi.any(),
  isAMNameDisabled: Joi.any(),
  aLastName: Joi.any(),
  aPosition: Joi.any(),
  aEmailAddress: Joi.any(),
  aContactNumber: Joi.any(),
  aDateOfBirth: Joi.any(),
  aMothersMaidenName: Joi.any(),
  aGender: Joi.any(),
  aAubNationalityCode: Joi.any(),
  aAubDeviceId: Joi.any(),
  aAubIdCode: Joi.any(),
  aAubIdNumber: Joi.any(),
  aAubRegionCode: Joi.any(),

  settlements: Joi.any(),

  isWantPackworksWallet: Joi.any(),
  isWantPayMayaBankWallet: Joi.any(),
  isWantPayMayaBankAccount: Joi.any(),

  idsOfValidSignatories: Joi.any(),
  idsOfValidSignatoriesBack: Joi.any(),
  idsOfValidSignatoriesSignature: Joi.any(),

  barangayBusinessPermit: Joi.any(),
  barangayBusinessPermitBack: Joi.any(),
  barangayBusinessPermitSignature: Joi.any(),

  storePhoto: Joi.any(),

  mayorsOrBusinessPermit: Joi.any(),
  mayorsOrBusinessPermitBack: Joi.any(),
  mayorsOrBusinessPermitSignature: Joi.any(),

  birCert2303: Joi.any(),
  birCert2303Back: Joi.any(),
  birCert2303Signature: Joi.any(),

  secCert: Joi.any(),
  secCertBack: Joi.any(),
  secCertSignature: Joi.any(),

  aoiAndByLaws: Joi.any(),
  aoiAndByLawsBack: Joi.any(),
  aoiAndByLawsSignature: Joi.any(),

  gis: Joi.any(),
  gisBack: Joi.any(),
  gisSignature: Joi.any(),

  secretarysCert: Joi.any(),
  secretarysCertBack: Joi.any(),
  secretarysCertSignature: Joi.any(),

  agreeTermsPrivacy: Joi.any(),
  // bldgNameNo: Joi.any(),
});

export const GeneralInfoStarterSchema = Joi.object({
  merchantName: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Company name is required.',
    'string.base': 'Company name is invalid.',
    'string.empty': 'Company name is invalid.',
    'string.min': 'Company name can be at most 64 characters only.',
    'string.max': 'Company name can be at most 64 characters only.',
  }),
  merchantBusinessName: Joi.any(),
  businessHandle: Joi.any(),
  merchantCompanyTin: Joi.string()
    .trim()
    .length(9)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'TIN is required.',
      'string.base': 'TIN is invalid.',
      'string.empty': 'TIN is invalid.',
      'string.length': 'TIN must be 9 digits',
      'string.patter.base': 'TIN must only contain digits',
    }),
  mainOfficeContactNumber: Joi.string()
    .trim()
    .min(1)
    .max(30)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Contact number is required.',
      'string.base': 'Contact number is invalid.',
      'string.empty': 'Contact number is invalid.',
      'string.pattern.base': 'Contact number is invalid.',
      'string.min': 'Contact number can be at most 30 characters only.',
      'string.max': 'Contact number can be at most 30 characters only.',
    }),
  mainOfficeBldgNameNo: Joi.string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .allow('')
    .messages({
      'string.base': 'Building name and number is invalid.',
      'string.empty': 'Building name and number is invalid.',
      'string.min':
        'Building name and number can be at most 200 characters only.',
      'string.max':
        'Building name and number can be at most 200 characters only.',
    }),
  mainOfficeStreetAddress: Joi.string()
    .trim()
    .min(1)
    .max(64)
    .required()
    .messages({
      'any.required': 'Street address is required.',
      'string.base': 'Street address is invalid.',
      'string.empty': 'Street address is invalid.',
      'string.min': 'Street address can be at most 64 characters only.',
      'string.max': 'Street address can be at most 64 characters only.',
    }),
  mainOfficeBarangay: Joi.string().trim().min(1).max(200).required().messages({
    'any.required': 'Barangay is required.',
    'string.base': 'Barangay is invalid.',
    'string.empty': 'Barangay is invalid.',
    'string.min': 'Barangay can be at most 200 characters only.',
    'string.max': 'Barangay can be at most 200 characters only.',
  }),
  mainOfficeCity: Joi.string().trim().min(1).max(100).required().messages({
    'any.required': 'City is required.',
    'string.base': 'City is invalid.',
    'string.empty': 'City is invalid.',
    'string.min': 'City can be at most 100 characters only.',
    'string.max': 'City can be at most 100 characters only.',
  }),
  mainOfficeProvince: Joi.string().trim().min(1).max(64).required().messages({
    'any.required': 'Province is required.',
    'string.base': 'Province is invalid.',
    'string.empty': 'Province is invalid.',
    'string.min': 'Province can be at most 64 characters only.',
    'string.max': 'Province can be at most 64 characters only.',
  }),
  mainOfficePostalCode: Joi.string()
    .trim()
    .min(4)
    .max(10)
    .regex(stringWithNumbersOnlyRegEx)
    .required()
    .messages({
      'any.required': 'Postal code is required.',
      'string.base': 'Postal code is invalid.',
      'string.empty': 'Postal code is invalid.',
      'string.min': 'Postal code must have 4 to 10 characters only.',
      'string.max': 'Postal code must have 4 to 10 characters only.',
      'string.pattern.base': 'Postal code is invalid.',
    }),
  isBrickAndMortarStore: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  industry: Joi.string().trim().min(1).max(200).required().messages({
    'any.required': 'Industry is required.',
    'string.base': 'Industry is invalid.',
    'string.empty': 'Industry is invalid.',
    'string.min': 'Industry must have 1 to 200 characters only.',
    'string.max': 'Industry must have 1 to 200 characters only.',
  }),
  serviceDescription: Joi.string().trim().required().messages({
    'any.required': 'Service description is required.',
    'string.base': 'Service description is invalid.',
    'string.empty': 'Service description is invalid.',
  }),
  modeOfPayment: Joi.array()
    .items(ModeOfPaymentSchema)
    .min(1)
    .required()
    .messages({
      'any.required': 'Mode of payments is required.',
      'array.base': 'Mode of payments must be an array.',
      'array.includesRequiredUnknowns': 'Mode of payments has invalid items.',
      'array.min': 'Please select at least 1 mode of payment',
    }),
  currency: Joi.number().valid(Currency.PHP, Currency.USD).required().messages({
    'any.only': 'Currency is invalid.',
    'any.required': 'Currency is required.',
    'number.base': 'Currency is invalid.',
  }),
  aubPartnerId: Joi.string().trim().min(0).optional().messages({
    'string.base': 'AUB partner ID is invalid.',
    'string.empty': 'AUB partner ID is invalid.',
  }),

  estimatedMonthlyGrossTransaction: Joi.string()
    .trim()
    .min(0)
    .optional()
    .messages({
      'string.base': 'Estimated Monthly Gross Transaction is invalid.',
      'string.empty': 'Estimated Monthly Gross Transaction is invalid.',
    }),
  businessWebsiteUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Business website is invalid.',
    'string.empty': 'Business website is invalid.',
  }),
  isBusinessWebsiteUnderDevelopment: Joi.boolean().required().messages({
    'any.required': 'Field is required.',
    'boolean.base': 'Field is invalid.',
  }),
  facebookUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Facebook field is invalid.',
    'string.empty': 'Facebook field is invalid.',
  }),
  twitterUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Twitter field is invalid.',
    'string.empty': 'Twitter field is invalid.',
  }),
  instagramUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Instagram field is invalid.',
    'string.empty': 'Instagram field is invalid.',
  }),
  tiktokUrl: Joi.string().trim().min(0).optional().messages({
    'string.base': 'Tiktok field is invalid.',
    'string.empty': 'Tiktok field is invalid.',
  }),

  businessTypeId: Joi.any(),

  aFirstName: Joi.any(),
  aMiddleName: Joi.any(),
  isAMNameDisabled: Joi.any(),
  aLastName: Joi.any(),
  aPosition: Joi.any(),
  aEmailAddress: Joi.any(),
  aContactNumber: Joi.any(),
  aDateOfBirth: Joi.any(),
  aMothersMaidenName: Joi.any(),
  aGender: Joi.any(),
  aAubNationalityCode: Joi.any(),
  aAubDeviceId: Joi.any(),
  aAubIdCode: Joi.any(),
  aAubIdNumber: Joi.any(),
  aAubRegionCode: Joi.any(),

  settlements: Joi.any(),

  isWantPackworksWallet: Joi.any(),
  isWantPayMayaBankWallet: Joi.any(),
  isWantPayMayaBankAccount: Joi.any(),

  idsOfValidSignatories: Joi.any(),
  idsOfValidSignatoriesBack: Joi.any(),
  idsOfValidSignatoriesSignature: Joi.any(),

  barangayBusinessPermit: Joi.any(),
  barangayBusinessPermitBack: Joi.any(),
  barangayBusinessPermitSignature: Joi.any(),

  storePhoto: Joi.any(),

  mayorsOrBusinessPermit: Joi.any(),
  mayorsOrBusinessPermitBack: Joi.any(),
  mayorsOrBusinessPermitSignature: Joi.any(),

  birCert2303: Joi.any(),
  birCert2303Back: Joi.any(),
  birCert2303Signature: Joi.any(),

  secCert: Joi.any(),
  secCertBack: Joi.any(),
  secCertSignature: Joi.any(),

  aoiAndByLaws: Joi.any(),
  aoiAndByLawsBack: Joi.any(),
  aoiAndByLawsSignature: Joi.any(),

  gis: Joi.any(),
  gisBack: Joi.any(),
  gisSignature: Joi.any(),

  secretarysCert: Joi.any(),
  secretarysCertBack: Joi.any(),
  secretarysCertSignature: Joi.any(),
  agreeTermsPrivacy: Joi.any(),
  // bldgNameNo: Joi.any(),
});
